import { useFormik } from "formik";
import React, { useContext, useEffect, useMemo, useState } from "react";
import * as Yup from "yup";

import { useAuth } from "@/context/AuthContext";
import { siteApi } from "@/lib/api";
import { showErrorToast, showSuccessToast } from "@/utils/toastUtils";
import { AppContext } from "../../context/AppContext";
import useSiteSettings from "@/hooks/useSiteSettings";
import AuthNoticeModal from "./AuthNoticeModal";

const PHONE_CARRIERS = ["SKT", "KT", "LG", "SKT Altel", "KT Altel", "LG Altel"];

const trimValue = (value) => String(value ?? "").trim();

const getRows = (response) => {
  const payload = response?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

const normalizeActiveBankList = (rows = []) =>
  [...(Array.isArray(rows) ? rows : [])]
    .filter((item) => item?.is_whether_to_use !== false)
    .sort((a, b) => {
      const leftSequence = Number(a?.sequence);
      const rightSequence = Number(b?.sequence);
      const left = Number.isFinite(leftSequence) ? leftSequence : Number.MAX_SAFE_INTEGER;
      const right = Number.isFinite(rightSequence) ? rightSequence : Number.MAX_SAFE_INTEGER;
      if (left !== right) return left - right;
      return Number(a?.id || 0) - Number(b?.id || 0);
    });

const getInitialReferralCode = () => {
  if (typeof window === "undefined") return "";

  const params = new URLSearchParams(window.location.search);
  return (
    params.get("referral_code") ||
    params.get("referral") ||
    params.get("ref") ||
    params.get("code") ||
    sessionStorage.getItem("affiliate_referral_code") ||
    localStorage.getItem("affiliate_referral_code") ||
    ""
  );
};

const SIGNUP_MESSAGE_MAP = {
  "Registration successfull": "회원가입이 완료되었습니다.",
  "Registration successful": "회원가입이 완료되었습니다.",
  "Registration failed": "회원가입에 실패했습니다.",
  Success: "사용 가능합니다.",
  Available: "사용 가능합니다.",
  Duplicate: "이미 사용 중입니다.",
  "Already exists": "이미 사용 중입니다.",
  "Something went wrong": "처리 중 오류가 발생했습니다.",
};

const getSignupMessage = (message, fallback = "처리 중 오류가 발생했습니다.") => {
  const text = trimValue(message);
  if (!text) return fallback;
  if (SIGNUP_MESSAGE_MAP[text]) return SIGNUP_MESSAGE_MAP[text];

  const lowerText = text.toLowerCase();
  if (lowerText.includes("nickname") && (lowerText.includes("exist") || lowerText.includes("duplicate"))) {
    return "이미 사용 중인 닉네임입니다.";
  }
  if ((lowerText.includes("id") || lowerText.includes("user")) && (lowerText.includes("exist") || lowerText.includes("duplicate"))) {
    return "이미 사용 중인 아이디입니다.";
  }
  if (lowerText.includes("referral") || lowerText.includes("affiliate")) return "가입코드를 확인해주세요.";
  if (lowerText.includes("valid payload")) return "입력값을 다시 확인해주세요.";
  return text;
};

const getRegisterFieldOrder = (settings = {}) => {
  const order = [
    "user_unique_id",
    "nick_name",
    "password",
    "verify_password",
    "depositor_name",
    "bank_id",
    "account_no",
    "exchange_password",
    "phone_carrier",
    "phone",
    "referral_code",
  ];

  if (settings?.use_date_of_birth) order.push("dob");
  if (settings?.use_of_gender) order.push("gender");
  return order;
};

const Register = ({ onSuccess, onSwitchToLogin }) => {
  const { updateLoggedIn } = useContext(AppContext);
  const { signUp } = useAuth();
  const { settings } = useSiteSettings();
  const [bankList, setBankList] = useState([]);
  const [bankError, setBankError] = useState("");
  const [noticeMessage, setNoticeMessage] = useState("");
  const [duplicateCheckStatus, setDuplicateCheckStatus] = useState({
    user_unique_id: { checked: false, isDuplicate: false, isChecking: false },
    nick_name: { checked: false, isDuplicate: false, isChecking: false },
  });

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        user_unique_id: Yup.string()
          .required("아이디를 입력해주세요.")
          .matches(/^[a-z0-9]+$/, "아이디는 영문 소문자와 숫자만 사용할 수 있습니다."),
        nick_name: Yup.string()
          .required("닉네임을 입력해주세요.")
          .matches(/^[가-힣a-zA-Z0-9]+$/, "닉네임은 문자와 숫자만 사용할 수 있습니다."),
        password: Yup.string().required("비밀번호를 입력해주세요.").min(6, "비밀번호는 6자 이상 입력해주세요."),
        verify_password: Yup.string()
          .required("비밀번호 확인을 입력해주세요.")
          .oneOf([Yup.ref("password")], "비밀번호가 일치하지 않습니다."),
        depositor_name: Yup.string().required("예금주명을 입력해주세요."),
        bank_id: Yup.string().required("은행을 선택해주세요."),
        account_no: Yup.string().required("계좌번호를 입력해주세요."),
        exchange_password: Yup.string().required("출금 비밀번호를 입력해주세요."),
        phone_carrier: Yup.string().required("통신사를 선택해주세요."),
        phone: Yup.string().required("휴대폰 번호를 입력해주세요."),
        referral_code: Yup.string().required("가입코드를 입력해주세요."),
        dob: settings?.use_date_of_birth
          ? Yup.string().required("생년월일을 입력해주세요.").matches(/^\d{6}-\d$/, "생년월일은 930801-1 형식으로 입력해주세요.")
          : Yup.string(),
        gender: settings?.use_of_gender ? Yup.string().required("성별을 선택해주세요.") : Yup.string(),
      }),
    [settings?.use_date_of_birth, settings?.use_of_gender]
  );

  const formik = useFormik({
    initialValues: {
      user_unique_id: "",
      nick_name: "",
      password: "",
      verify_password: "",
      depositor_name: "",
      bank_id: "",
      account_no: "",
      exchange_password: "",
      phone_carrier: "",
      phone: "",
      referral_code: getInitialReferralCode(),
      dob: "",
      gender: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const idCheck = duplicateCheckStatus.user_unique_id;
      const nickCheck = duplicateCheckStatus.nick_name;

      if (!idCheck.checked || idCheck.isDuplicate) {
        setNoticeMessage(idCheck.isDuplicate ? "이미 사용 중인 아이디입니다." : "아이디 중복확인을 해주세요.");
        setSubmitting(false);
        return;
      }

      if (!nickCheck.checked || nickCheck.isDuplicate) {
        setNoticeMessage(nickCheck.isDuplicate ? "이미 사용 중인 닉네임입니다." : "닉네임 중복확인을 해주세요.");
        setSubmitting(false);
        return;
      }

      const payload = {
        user_unique_id: trimValue(values.user_unique_id),
        nick_name: trimValue(values.nick_name),
        depositor_name: trimValue(values.depositor_name),
        bank_id: Number(values.bank_id),
        account_no: trimValue(values.account_no),
        exchange_password: trimValue(values.exchange_password),
        phone_carrier: trimValue(values.phone_carrier),
        phone: trimValue(values.phone),
        referral_code: trimValue(values.referral_code),
      };

      if (settings?.use_date_of_birth) payload.dob = trimValue(values.dob);
      if (settings?.use_of_gender) payload.gender = trimValue(values.gender);

      try {
        await signUp(values.user_unique_id, values.password, payload);
        updateLoggedIn(true);
        showSuccessToast("회원가입이 완료되었습니다.");
        onSuccess?.();
      } catch (error) {
        const message = getSignupMessage(error.message, "회원가입에 실패했습니다.");
        setNoticeMessage(message);
        showErrorToast(message);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    let isMounted = true;

    const loadBanks = async () => {
      const response = await siteApi.getBanks({ limit: "ALL" });
      if (!isMounted) return;

      if (response.success) {
        setBankList(normalizeActiveBankList(getRows(response)));
        setBankError("");
      } else {
        setBankError(response.error || "은행 목록을 불러오지 못했습니다.");
      }
    };

    loadBanks();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setDuplicateCheckStatus((prev) => ({
      ...prev,
      user_unique_id: { checked: false, isDuplicate: false, isChecking: false },
    }));
  }, [formik.values.user_unique_id]);

  useEffect(() => {
    setDuplicateCheckStatus((prev) => ({
      ...prev,
      nick_name: { checked: false, isDuplicate: false, isChecking: false },
    }));
  }, [formik.values.nick_name]);

  const isFieldInvalid = (name) => formik.touched[name] && formik.errors[name];

  const handleRegisterSubmit = async (event) => {
    event.preventDefault();

    const fieldOrder = getRegisterFieldOrder(settings);
    const touchedFields = fieldOrder.reduce((acc, fieldName) => {
      acc[fieldName] = true;
      return acc;
    }, {});

    formik.setTouched(touchedFields, false);
    const errors = await formik.validateForm();
    const firstErrorField = fieldOrder.find((fieldName) => errors[fieldName]);

    if (firstErrorField) {
      setNoticeMessage(errors[firstErrorField]);
      return;
    }

    formik.submitForm();
  };

  const verifyDuplicate = async (fieldName) => {
    const value = trimValue(formik.values[fieldName]);
    if (!value) {
      setNoticeMessage(fieldName === "user_unique_id" ? "아이디를 입력해주세요." : "닉네임을 입력해주세요.");
      return;
    }

    setDuplicateCheckStatus((prev) => ({
      ...prev,
      [fieldName]: { ...prev[fieldName], isChecking: true },
    }));

    const payload = {
      key_type: "USER",
      [fieldName]: value,
    };
    const response = await siteApi.verifyDuplicate(payload);

    if (response.success) {
      setDuplicateCheckStatus((prev) => ({
        ...prev,
        [fieldName]: { checked: true, isDuplicate: false, isChecking: false },
      }));
      showSuccessToast("사용 가능합니다.");
      return;
    }

    setDuplicateCheckStatus((prev) => ({
      ...prev,
      [fieldName]: { checked: true, isDuplicate: true, isChecking: false },
    }));
    showErrorToast(getSignupMessage(response.error, "이미 사용 중입니다."));
  };

  const getDuplicateButtonLabel = (fieldName) => {
    const status = duplicateCheckStatus[fieldName];
    if (status.isChecking) return "확인 중";
    if (!status.checked) return "중복확인";
    return status.isDuplicate ? "중복" : "사용가능";
  };

  const getDuplicateButtonClass = (fieldName) => {
    const status = duplicateCheckStatus[fieldName];
    if (!status.checked) return "";
    return status.isDuplicate ? "error" : "success";
  };

  return (
    <form className="auth-form" onSubmit={handleRegisterSubmit} noValidate>
      <AuthNoticeModal message={noticeMessage} onClose={() => setNoticeMessage("")} />

      <div className="auth-form-grid">
        <div className="form-group">
          <label htmlFor="register_user_unique_id" className="form-label">
            아이디 <span className="required">*</span>
          </label>
          <div className="auth-field-row">
            <input
              type="text"
              id="register_user_unique_id"
              name="user_unique_id"
              className="form-input"
              value={formik.values.user_unique_id}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              placeholder="영문 소문자/숫자"
              autoComplete="username"
            />
            <button
              type="button"
              className={`auth-inline-button ${getDuplicateButtonClass("user_unique_id")}`}
              onClick={() => verifyDuplicate("user_unique_id")}
              disabled={duplicateCheckStatus.user_unique_id.isChecking}
            >
              {getDuplicateButtonLabel("user_unique_id")}
            </button>
          </div>
          {isFieldInvalid("user_unique_id") ? (
            <div className="error-message">{formik.errors.user_unique_id}</div>
          ) : null}
        </div>

        <div className="form-group">
          <label htmlFor="register_nick_name" className="form-label">
            닉네임 <span className="required">*</span>
          </label>
          <div className="auth-field-row">
            <input
              type="text"
              id="register_nick_name"
              name="nick_name"
              className="form-input"
              value={formik.values.nick_name}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              placeholder="닉네임"
              autoComplete="nickname"
            />
            <button
              type="button"
              className={`auth-inline-button ${getDuplicateButtonClass("nick_name")}`}
              onClick={() => verifyDuplicate("nick_name")}
              disabled={duplicateCheckStatus.nick_name.isChecking}
            >
              {getDuplicateButtonLabel("nick_name")}
            </button>
          </div>
          {isFieldInvalid("nick_name") ? (
            <div className="error-message">{formik.errors.nick_name}</div>
          ) : null}
        </div>

        <div className="form-group">
          <label htmlFor="register_password" className="form-label">
            비밀번호 <span className="required">*</span>
          </label>
          <input
            type="password"
            id="register_password"
            name="password"
            className="form-input"
            value={formik.values.password}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            placeholder="6자 이상"
            autoComplete="new-password"
          />
          {isFieldInvalid("password") ? (
            <div className="error-message">{formik.errors.password}</div>
          ) : null}
        </div>

        <div className="form-group">
          <label htmlFor="register_verify_password" className="form-label">
            비밀번호 확인 <span className="required">*</span>
          </label>
          <input
            type="password"
            id="register_verify_password"
            name="verify_password"
            className="form-input"
            value={formik.values.verify_password}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            placeholder="비밀번호 재입력"
            autoComplete="new-password"
          />
          {isFieldInvalid("verify_password") ? (
            <div className="error-message">{formik.errors.verify_password}</div>
          ) : null}
        </div>

        <div className="form-group">
          <label htmlFor="register_depositor_name" className="form-label">
            예금주명 <span className="required">*</span>
          </label>
          <input
            type="text"
            id="register_depositor_name"
            name="depositor_name"
            className="form-input"
            value={formik.values.depositor_name}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            placeholder="예금주명"
          />
          {isFieldInvalid("depositor_name") ? (
            <div className="error-message">{formik.errors.depositor_name}</div>
          ) : null}
        </div>

        <div className="form-group">
          <label htmlFor="register_bank_id" className="form-label">
            은행 <span className="required">*</span>
          </label>
          <select
            id="register_bank_id"
            name="bank_id"
            className="form-select"
            value={formik.values.bank_id}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
          >
            <option value="">은행 선택</option>
            {bankList.map((bank) => (
              <option key={bank.id} value={bank.id}>
                {bank.bank_name}
              </option>
            ))}
          </select>
          {bankError ? <div className="error-message">{bankError}</div> : null}
          {isFieldInvalid("bank_id") ? (
            <div className="error-message">{formik.errors.bank_id}</div>
          ) : null}
        </div>

        <div className="form-group">
          <label htmlFor="register_account_no" className="form-label">
            계좌번호 <span className="required">*</span>
          </label>
          <input
            type="text"
            id="register_account_no"
            name="account_no"
            className="form-input"
            value={formik.values.account_no}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            placeholder="계좌번호"
          />
          {isFieldInvalid("account_no") ? (
            <div className="error-message">{formik.errors.account_no}</div>
          ) : null}
        </div>

        <div className="form-group">
          <label htmlFor="register_exchange_password" className="form-label">
            출금 비밀번호 <span className="required">*</span>
          </label>
          <input
            type="password"
            id="register_exchange_password"
            name="exchange_password"
            className="form-input"
            value={formik.values.exchange_password}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            placeholder="출금 비밀번호"
            autoComplete="new-password"
          />
          {isFieldInvalid("exchange_password") ? (
            <div className="error-message">{formik.errors.exchange_password}</div>
          ) : null}
        </div>

        <div className="form-group">
          <label htmlFor="register_phone_carrier" className="form-label">
            통신사 <span className="required">*</span>
          </label>
          <select
            id="register_phone_carrier"
            name="phone_carrier"
            className="form-select"
            value={formik.values.phone_carrier}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
          >
            <option value="">통신사 선택</option>
            {PHONE_CARRIERS.map((carrier) => (
              <option key={carrier} value={carrier}>
                {carrier}
              </option>
            ))}
          </select>
          {isFieldInvalid("phone_carrier") ? (
            <div className="error-message">{formik.errors.phone_carrier}</div>
          ) : null}
        </div>

        <div className="form-group">
          <label htmlFor="register_phone" className="form-label">
            휴대폰 번호 <span className="required">*</span>
          </label>
          <input
            type="text"
            id="register_phone"
            name="phone"
            className="form-input"
            value={formik.values.phone}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            placeholder="01012345678"
            autoComplete="tel"
          />
          {isFieldInvalid("phone") ? (
            <div className="error-message">{formik.errors.phone}</div>
          ) : null}
        </div>

        <div className="form-group">
          <label htmlFor="register_referral_code" className="form-label">
            가입코드 <span className="required">*</span>
          </label>
          <input
            type="text"
            id="register_referral_code"
            name="referral_code"
            className="form-input"
            value={formik.values.referral_code}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            placeholder="가입코드"
          />
          {isFieldInvalid("referral_code") ? (
            <div className="error-message">{formik.errors.referral_code}</div>
          ) : null}
        </div>

        {settings?.use_date_of_birth ? (
          <div className="form-group">
            <label htmlFor="register_dob" className="form-label">
              생년월일 <span className="required">*</span>
            </label>
            <input
              type="text"
              id="register_dob"
              name="dob"
              className="form-input"
              value={formik.values.dob}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              placeholder="930801-1"
            />
            {isFieldInvalid("dob") ? (
              <div className="error-message">{formik.errors.dob}</div>
            ) : null}
          </div>
        ) : null}

        {settings?.use_of_gender ? (
          <div className="form-group">
            <label htmlFor="register_gender" className="form-label">
              성별 <span className="required">*</span>
            </label>
            <select
              id="register_gender"
              name="gender"
              className="form-select"
              value={formik.values.gender}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            >
              <option value="">성별 선택</option>
              <option value="MALE">남성</option>
              <option value="FEMALE">여성</option>
              <option value="OTHER">기타</option>
            </select>
            {isFieldInvalid("gender") ? (
              <div className="error-message">{formik.errors.gender}</div>
            ) : null}
          </div>
        ) : null}
      </div>

      <button type="submit" className="register-button" disabled={formik.isSubmitting}>
        {formik.isSubmitting ? (
          <>
            <span className="auth-spinner" aria-hidden="true" />
            가입 중
          </>
        ) : (
          "회원가입"
        )}
      </button>

      <button type="button" className="auth-switch-button" onClick={onSwitchToLogin}>
        이미 계정이 있으신가요? 로그인
      </button>
    </form>
  );
};

export default Register;
